import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  "YOU HAVE CHANGES THAT HAVE NOT BEEN SAVED."
 *
 *  ⚠️ NOT `useBlocker`, AND THE REASON IS MEASURED RATHER THAN STYLISTIC.
 *
 *  `react-router@8.3.0` exports `useBlocker`, and it throws outside a DATA router:
 *  `useDataRouterContext("useBlocker")` is the first thing it calls. `App.tsx` mounts
 *  `<BrowserRouter>` — the declarative router — and `renderWithProviders` mounts
 *  `<MemoryRouter>`. Migrating to `createBrowserRouter` to get one hook would rewrite the
 *  app's entry point, the test harness every page test uses and the nuqs adapter binding,
 *  which is a large blast radius to buy a confirm dialog.
 *
 *  So the guard is THREE listeners, and between them they cover every way out:
 *
 *    • `beforeunload` for a reload, a close and a typed URL — the browser's own dialog,
 *      which is the only thing that can interrupt those.
 *    • A CAPTURE-PHASE click listener on `document` for in-app navigation. Every
 *      destination in this shell is a react-router link that renders a real `<a href>` —
 *      [amended TYPED-BACK] the sidebar's rows are `Link`s now, not `NavLink`s — so one
 *      listener catches the sidebar, the ⌘K palette's links and any breadcrumb, without
 *      each of them knowing this hook exists.
 *    • ⚠️ [D4.4 §6] `popstate`, for the BACK BUTTON and the trackpad swipe.
 *
 *  ⚠️ CAPTURE PHASE, NOT BUBBLE. React Router's own click handler is bound on the anchor,
 *  so a bubble-phase listener runs AFTER navigation has already been requested. Capture
 *  runs first, which is the only point at which `preventDefault` still means anything.
 *
 *  It is fully testable in jsdom, which `useBlocker` under a `MemoryRouter` is not.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.4 §6] THE THIRD LISTENER, AND WHY IT IS HERE RATHER THAN BESIDE THIS HOOK.
 *
 *  A history navigation fires NO click and unloads NOTHING, so both listeners above were
 *  blind to it: `beforeunload` does not fire for a same-origin SPA navigation, and there is
 *  no anchor for the capture listener to intercept. `grep popstate web/src` returned ZERO
 *  before this — so the browser's Back button silently discarded a half-built arrangement,
 *  and the macOS two-finger swipe did the same thing without even a button press.
 *
 *  ⚠️ IT IS A THIRD LISTENER IN THE SAME HOOK, NEVER A SECOND GUARD. One `when`, one
 *  `confirm`, one modal. A parallel guard beside this one would be two dirty checks that
 *  disagree the first time a page sets one and forgets the other.
 *
 *  ⚠️ THE SENTINEL IS PUSHED BACK BEFORE THE MODAL IS RAISED, NOT AFTER. `confirm` is
 *  async; React Router's own `popstate` listener is synchronous and has already re-rendered
 *  another route by the time an `await` resolves. Restoring the entry first means the
 *  operator is asked the question while still looking at their form.
 *
 *  ⚠️ THE SENTINEL CARRIES React Router's OWN `history.state` FORWARD. Its `idx` and `key`
 *  are how the declarative router tracks position; pushing a bare `null` over them makes
 *  the router's next navigation reason from a state it did not write.
 *
 *  ⚠️ THE COST, STATED: while the form is dirty there is ONE extra history entry, and it is
 *  not removed on disarm. A cleanup that called `history.back()` would navigate at exactly
 *  the moment the component is unmounting — which is a real navigation to fix a bookkeeping
 *  detail. The entry has the same URL as the page it sits on, so the only visible effect is
 *  that Back must be pressed once more after saving. That is the cheaper of the two costs.
 *
 *  ⚠️ AND THE CSS HALF IS IN `styles/index.css`. `overscroll-behavior-x: none` stops the
 *  swipe from generating a navigation at all in Chromium — but nothing this repository owns
 *  can MEASURE that, so this listener is what makes the guarantee real, and it covers the
 *  Back button too.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [AUTO-SAVE] "PUSHED ONCE, WHEN THE GUARD ARMS" WAS FALSE, AND THREE RULES MAKE IT TRUE.
 *
 *  Every caller passes `confirm` as an inline arrow, so the effect below re-ran on EVERY render
 *  of its page and pushed a sentinel each time — a form re-renders per keystroke, so typing ten
 *  characters stacked ten entries. Back then landed on a sentinel of the same page, and once the
 *  operator answered "Leave", `go(-2)` landed on another one and the question was asked again.
 *  The round-the-clock form had it whenever it was dirty; the arrangement builder had it on its
 *  UNLOAD arm alone, where no popstate listener even exists; and `ENABLE_AUTO_SAVE=false` arms
 *  the builder's popstate path for a whole sitting, which would have made it the ordinary case.
 *
 *    1. AN ENTRY IS PUSHED ONLY WHILE THE POPSTATE PATH IS ARMED (`when`). It exists for the Back
 *       press to land on; with only `beforeunload` armed nothing intercepts Back, so an entry
 *       there costs the operator a press and protects nothing.
 *    2. NOT WHILE STANDING ON ONE ALREADY. A re-render, or a disarm-and-re-arm on the entry this
 *       hook pushed, reuses it — which is what "one extra entry" in the cost above always meant.
 *    3. THE RESTORED ENTRY CARRIES THE PAGE'S ADDRESS AS OF ITS LAST RENDER, never the address
 *       Back just landed on. The arrangement builder rewrites its own address on a new order's
 *       first save (`/new` → `/:id/edit`, with `replaceState`), so the entry below the sentinel
 *       still reads `/new`; restoring with THAT left an operator who chose "Stay" on a saved
 *       draft whose address bar said "new arrangement". A render is never later than the arming,
 *       so this is never staler than the old answer.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Marks the entry this hook pushed, so nothing else is mistaken for it. */
const SENTINEL = '__pamsUnsavedGuard';

/** [AUTO-SAVE] Rule 2 — is the current entry one this hook pushed? `history.state` is untyped. */
const standingOnSentinel = (): boolean =>
  (window.history.state as Readonly<Record<string, unknown>> | null)?.[SENTINEL] === true;

export type UnsavedGuardOptions = {
  /** Armed only while there is genuinely something to lose. */
  when: boolean;
  /**
   * ⚠️ [amended C1f §6] AN IN-APP NAVIGATION CAN BE FOLLOWED BY A WRITE; AN UNLOAD CANNOT.
   *
   * `beforeunload` is the one event the browser will not let an async request finish inside,
   * so a page that AUTOSAVES on unmount still needs the browser's own dialog for anything the
   * server has not acknowledged — while its click and `popstate` paths should only stop the
   * operator for work autosave genuinely cannot save.
   *
   * ⚠️ OPTIONAL, DEFAULTING TO `when`, so every existing caller is byte-identical.
   * `StandingOrderBuilder` saves explicitly and passes nothing.
   */
  whenUnload?: boolean;
  /** Return `true` to let the navigation through. Typically a `modal.confirm`. */
  confirm: (href: string) => Promise<boolean> | boolean;
};

export const useUnsavedGuard = ({ when, whenUnload, confirm }: UnsavedGuardOptions): void => {
  const unloadArmed = whenUnload ?? when;
  /*
   * ⚠️ [AUTO-SAVE] Rule 3 — the page's address as of its last render. A layout effect with no
   * dependency list, because a ref written during render is what `react-hooks/refs` refuses, and
   * it must be current before the popstate handler below could ever read it.
   */
  const pageHref = useRef('');
  useLayoutEffect(() => {
    pageHref.current = window.location.href;
  });

  useEffect(() => {
    if (!when && !unloadArmed) return;

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Browsers ignore any custom string now and show their own wording; assigning
      // `returnValue` is still what arms the dialog at all.
      event.preventDefault();
      event.returnValue = '';
    };

    const onClickCapture = (event: MouseEvent): void => {
      // A modified click is the user deliberately opening a new tab, which loses nothing.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href') ?? '';
      // An in-page anchor is the section checklist scrolling, not a navigation.
      if (href.startsWith('#') || href === window.location.pathname) return;

      event.preventDefault();
      event.stopPropagation();

      void (async () => {
        if (!(await confirm(href))) return;
        /*
         * ⚠️ DISARM `beforeunload` BEFORE LEAVING, OR THE OPERATOR IS ASKED TWICE.
         *
         * `location.assign` is a real unload, so the browser's own dialog fires straight
         * after this app's — two prompts for one decision, and the second one cannot say
         * what it is about because browsers ignore custom wording. The answer has already
         * been given; the browser has nothing left to ask.
         *
         * Removing the listener rather than flipping `when` because `when` belongs to the
         * caller and would not have taken effect until the next render anyway.
         */
        window.removeEventListener('beforeunload', onBeforeUnload);
        window.location.assign(href);
      })();
    };

    /*
     * ⚠️ THE SENTINEL IS PUSHED ONCE, WHEN THE GUARD ARMS. Without an extra entry there is
     * nothing for the first Back press to land on: it would leave the app outright, and by
     * the time `popstate` fired the document would already be unloading.
     *
     * The URL is unchanged, so nothing on screen moves and no link becomes wrong.
     *
     * ⚠️ [AUTO-SAVE] ONCE — rules 1 and 2 in the header. This effect re-runs on every render that
     * hands it a new `confirm`, and an unconditional push here stacked an entry per keystroke.
     */
    const pushSentinel = (href: string): void => {
      window.history.pushState({ ...window.history.state, [SENTINEL]: true }, '', href);
    };
    if (when && !standingOnSentinel()) pushSentinel(window.location.href);

    const onPopState = (): void => {
      /*
       * ⚠️ RESTORE FIRST, ASK SECOND. `confirm` is async and React Router's own listener is
       * not, so anything awaited here happens after the router has already moved. Pushing
       * the entry back immediately means the operator answers the question while still
       * looking at the form they are about to lose.
       *
       * ⚠️ [AUTO-SAVE] With the page's own address (rule 3), never `window.location.href`, which
       * by now is the entry Back landed on.
       */
      pushSentinel(pageHref.current || window.location.href);

      void (async () => {
        if (!(await confirm(window.location.href))) return;
        /*
         * ⚠️ DISARM `beforeunload` FIRST, for the same reason the click path does: the
         * answer has already been given and the browser has nothing left to ask.
         */
        window.removeEventListener('beforeunload', onBeforeUnload);
        /*
         * ⚠️ `-2`, NOT `-1`. The stack is [ …, whatever came before, this page, sentinel ]
         * and we are standing on the sentinel again after the push above — so one step back
         * merely returns to this page. Two steps is the navigation the operator asked for.
         */
        window.history.go(-2);
      })();
    };

    /* ⚠️ EACH LISTENER ON ITS OWN FLAG. `beforeunload` is the broader one — see `whenUnload`. */
    if (unloadArmed) window.addEventListener('beforeunload', onBeforeUnload);
    if (when) {
      window.addEventListener('popstate', onPopState);
      document.addEventListener('click', onClickCapture, true);
    }
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [when, unloadArmed, confirm]);
};
